import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import PremiumLock from '../components/PremiumLock';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ChevronLeft, ChevronRight, Check, Plus, ChevronDown, 
  Edit2, Target, Calendar as CalendarIcon, FolderPlus, LayoutGrid,
  Lock, Trophy, Ban, Archive, TrendingUp, GripVertical, ArrowRight, FolderOpen, Users
} from 'lucide-react';
import { 
  startOfWeek, endOfWeek, eachDayOfInterval, format, 
  addWeeks, subWeeks, isFuture, isToday
} from 'date-fns';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { ScrollArea } from '../components/ui/scroll-area';

// Group color palette for distinct styling - Dark theme
const GROUP_COLORS = [
  { bg: 'bg-blue-500/10', border: 'border-blue-500/30', accent: 'bg-blue-500', text: 'text-blue-400', badge: 'bg-blue-500/20 text-blue-400' },
  { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', accent: 'bg-emerald-500', text: 'text-emerald-400', badge: 'bg-emerald-500/20 text-emerald-400' },
  { bg: 'bg-purple-500/10', border: 'border-purple-500/30', accent: 'bg-purple-500', text: 'text-purple-400', badge: 'bg-purple-500/20 text-purple-400' },
  { bg: 'bg-[#FDF0EC]0/10', border: 'border-[#F5D5CB]', accent: 'bg-[#FDF0EC]0', text: 'text-coral', badge: 'bg-[#FDF0EC]0/20 text-coral' },
  { bg: 'bg-rose-500/10', border: 'border-rose-500/30', accent: 'bg-rose-500', text: 'text-rose-400', badge: 'bg-rose-500/20 text-rose-400' },
  { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', accent: 'bg-cyan-500', text: 'text-cyan-400', badge: 'bg-cyan-500/20 text-cyan-400' },
  { bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', accent: 'bg-indigo-500', text: 'text-indigo-400', badge: 'bg-indigo-500/20 text-indigo-400' },
  { bg: 'bg-coral/10', border: 'border-coral/30', accent: 'bg-coral', text: 'text-orange-400', badge: 'bg-coral/20 text-orange-400' },
];

export default function WeeklyTracker() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sharedGoals, setSharedGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [expandedGroups, setExpandedGroups] = useState({});
  const [isNewGroupDialogOpen, setIsNewGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [draggedGoal, setDraggedGoal] = useState(null);
  
  // Edit goal state
  const [editGoalOpen, setEditGoalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editTarget, setEditTarget] = useState(5);
  const [editEndDate, setEditEndDate] = useState(new Date());

  const days = eachDayOfInterval({
    start: currentWeekStart,
    end: endOfWeek(currentWeekStart, { weekStartsOn: 1 })
  });

  // Calculate weekly stats
  const weeklyStats = useMemo(() => {
    let completedDays = 0;
    let totalLogs = 0;
    
    goals.forEach(goal => {
      days.forEach(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const logEntry = goal.daily_logs?.find(l => l.date === dateStr);
        if (logEntry && logEntry.value > 0) {
          totalLogs++;
        }
      });
    });

    days.forEach(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const hasLog = goals.some(g => g.daily_logs?.some(l => l.date === dateStr && l.value > 0));
      if (hasLog) completedDays++;
    });

    const completionRate = goals.length > 0 && days.length > 0 
      ? Math.round((totalLogs / (goals.length * days.filter(d => !isFuture(d)).length)) * 100) || 0
      : 0;

    return { completedDays, totalLogs, completionRate };
  }, [goals, days]);

  useEffect(() => {
    if (user?.is_premium) {
      fetchData();
    }
  }, [user?.is_premium]);

  // Group active goals by group_id
  const groupedGoals = useMemo(() => {
    const grouped = {};
    groups.forEach(g => grouped[g.id] = []);
    goals.filter(g => g.status === 'ACTIVE' || g.status === 'PAUSED').forEach(goal => {
      if (grouped[goal.group_id]) {
        grouped[goal.group_id].push(goal);
      }
    });
    return grouped;
  }, [goals, groups]);

  // Get color for group
  const getGroupColor = (index) => GROUP_COLORS[index % GROUP_COLORS.length];

  if (!user?.is_premium) {
    return (
      <div className={cn(
        "min-h-screen transition-colors duration-300",
        isDark ? "bg-[#050505]" : "bg-[#FAF9F5]"
      )}>
        <Navbar />
        <div className="max-w-7xl mx-auto p-4 md:p-8">
          <PremiumLock featureName="Weekly Tracker" />
        </div>
      </div>
    );
  }

  const fetchData = async () => {
    try {
      const [goalsRes, groupsRes, sharedRes] = await Promise.all([
        api.get('/api/goals'),
        api.get('/api/groups'),
        api.get('/api/shared-goals').catch(() => ({ data: [] }))
      ]);
      setGoals(goalsRes.data);
      setGroups(groupsRes.data);
      setSharedGoals(sharedRes.data.filter(g => g.status === 'ACTIVE'));
      
      // Expand all groups by default
      const expanded = { 'shared-goals': true };
      groupsRes.data.forEach(g => expanded[g.id] = true);
      setExpandedGroups(expanded);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load tracker data");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLog = async (goal, day) => {
    if (isFuture(day)) {
      toast.error("Cannot log progress for future dates");
      return;
    }

    const dateStr = format(day, 'yyyy-MM-dd');
    const existingLog = goal.daily_logs?.find(l => l.date === dateStr);
    
    let newValue = 1.0;
    if (goal.tracking_type === 'binary') {
      if (existingLog) newValue = 0;
    } else {
      newValue = (existingLog?.value || 0) + 1;
    }

    const updatedGoals = goals.map(g => {
      if (g.id === goal.id) {
        const newLogs = g.daily_logs?.filter(l => l.date !== dateStr) || [];
        if (!(goal.tracking_type === 'binary' && existingLog)) {
          newLogs.push({ date: dateStr, value: newValue, timestamp: new Date().toISOString() });
        }
        return { ...g, daily_logs: newLogs };
      }
      return g;
    });
    setGoals(updatedGoals);

    try {
      await api.post(`/api/goals/${goal.id}/log`, {
        date: day.toISOString(),
        value: newValue
      });
    } catch (e) {
      toast.error("Failed to save progress");
      fetchData();
    }
  };

  const handleSharedGoalLog = async (goalId, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await api.put(`/api/shared-goals/${goalId}/progress`, {
        progress_delta: 1
      });
      
      toast.success(`+1 logged! (${res.data.current_progress}/${res.data.target_value})`);
      
      // Update local state for immediate feedback
      setSharedGoals(prev => prev.map(g => 
        g.id === goalId 
          ? { ...g, user_progress: res.data.current_progress }
          : g
      ));
      
      if (res.data.status === 'COMPLETED') {
        toast.success('Shared goal completed! 🎉');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log progress");
    }
  };

  const createGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      const res = await api.post('/api/groups', { name: newGroupName, order: groups.length });
      setGroups([...groups, res.data]);
      setExpandedGroups({ ...expandedGroups, [res.data.id]: true });
      setNewGroupName("");
      setIsNewGroupDialogOpen(false);
      toast.success("Group created!");
    } catch (e) {
      toast.error("Failed to create group");
    }
  };

  const handleChangeGroup = async (goalId, newGroupId) => {
    try {
      await api.patch(`/api/goals/${goalId}`, { group_id: newGroupId });
      setGoals(goals.map(g => g.id === goalId ? { ...g, group_id: newGroupId } : g));
      toast.success('Goal moved to new group');
    } catch (e) {
      console.error(e);
      toast.error('Failed to move goal');
    }
  };

  const handleDragStart = (e, goal) => {
    setDraggedGoal(goal);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetGroupId) => {
    e.preventDefault();
    if (draggedGoal && draggedGoal.group_id !== targetGroupId) {
      await handleChangeGroup(draggedGoal.id, targetGroupId);
    }
    setDraggedGoal(null);
  };

  const handleDragEnd = () => {
    setDraggedGoal(null);
  };

  const openEditGoal = (goal) => {
    setEditingGoal(goal);
    setEditTitle(goal.title);
    setEditTarget(goal.target_count);
    setEditEndDate(goal.end_date ? new Date(goal.end_date) : new Date());
    setEditGoalOpen(true);
  };

  const saveEditGoal = async () => {
    if (!editingGoal) return;
    try {
      await api.patch(`/api/goals/${editingGoal.id}`, {
        title: editTitle,
        target_count: parseInt(editTarget),
        end_date: editEndDate.toISOString()
      });
      toast.success("Goal Updated!");
      setEditGoalOpen(false);
      fetchData();
    } catch (e) {
      toast.error("Failed to update goal");
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const nextWeek = () => setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  const prevWeek = () => setCurrentWeekStart(subWeeks(currentWeekStart, 1));

  const activeGoals = goals.filter(g => g.status === 'ACTIVE' || g.status === 'PAUSED');
  const pastGoals = goals.filter(g => g.status === 'COMPLETED' || g.status === 'STOPPED' || g.status === 'FAILED');

  return (
    <div className={cn(
      "min-h-screen transition-colors duration-300",
      isDark ? "bg-[#050505]" : "bg-[#FAF9F5]"
    )}>
      <Navbar />
      
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Clean header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
          <div>
            <p className="sl-section-tag mb-2">WEEKLY TRACKER</p>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.03em] text-[#1A1A1A] leading-tight">
              This week.
            </h1>
            <p className="mt-2 text-sm text-[#5F5F5F]">Click any cell to log progress.</p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Unified stat chips */}
            <div className="hidden md:flex items-center gap-2">
              <div className="sl-chip">
                <CalendarIcon className="w-3.5 h-3.5" />
                <span className="font-semibold">{weeklyStats.completedDays}</span>
                <span className="opacity-70">days</span>
              </div>
              <div className="sl-chip">
                <Check className="w-3.5 h-3.5" />
                <span className="font-semibold">{weeklyStats.totalLogs}</span>
                <span className="opacity-70">logs</span>
              </div>
              <div className="sl-chip-coral">
                <TrendingUp className="w-3.5 h-3.5" />
                <span className="font-semibold">{weeklyStats.completionRate}%</span>
              </div>
            </div>

            {/* Week Navigator */}
            <div className="flex items-center rounded-full border bg-white border-[#EEEAE0] shadow-sm">
              <Button variant="ghost" size="icon" onClick={prevWeek} className="h-8 w-8 rounded-l-full text-[#9E8778] hover:text-[#1A1A1A] hover:bg-[#F4F3EE]">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="px-3 py-1 text-xs font-medium min-w-[120px] text-center text-[#5F5F5F] font-mono tracking-wider">
                {format(currentWeekStart, 'MMM d')} – {format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'd')}
              </div>
              <Button variant="ghost" size="icon" onClick={nextWeek} disabled={isFuture(addWeeks(currentWeekStart, 1))} className="h-8 w-8 rounded-r-full text-[#9E8778] hover:text-[#1A1A1A] hover:bg-[#F4F3EE]">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            <Button
              onClick={() => setIsNewGroupDialogOpen(true)}
              size="sm"
              className="sl-pill-dark h-8 px-4 text-xs"
            >
              <FolderPlus className="w-3.5 h-3.5" /> Group
            </Button>
          </div>
        </div>

        {/* Mobile Stats */}
        <div className="md:hidden flex items-center justify-center gap-3 flex-wrap">
          <div className="sl-chip">
            <CalendarIcon className="w-3.5 h-3.5" />
            <span className="font-semibold">{weeklyStats.completedDays}</span>
            <span className="opacity-70">days</span>
          </div>
          <div className="sl-chip">
            <Check className="w-3.5 h-3.5" />
            <span className="font-semibold">{weeklyStats.totalLogs}</span>
            <span className="opacity-70">logs</span>
          </div>
          <div className="sl-chip-coral">
            <TrendingUp className="w-3.5 h-3.5" />
            <span className="font-semibold">{weeklyStats.completionRate}%</span>
          </div>
        </div>

        {/* Main Content: Left Calendar + Right Group Management */}
        <div className="grid lg:grid-cols-[1fr_320px] gap-4">
          {/* LEFT: Compact Weekly Calendar */}
          <Card className={cn(
            "overflow-hidden shadow-sm",
            isDark ? "glass-card" : "bg-white border border-[#EEEAE0]"
          )}>
            <div className="overflow-x-auto">
              <div className="min-w-[600px]">
                {/* Header Row */}
                <div className={cn(
                  "flex border-b",
                  isDark ? "border-zinc-700 bg-zinc-800/50" : "border-[#EEEAE0] bg-white"
                )}>
                  <div className={cn(
                    "w-[160px] px-3 py-2 text-xs font-medium uppercase tracking-wider border-r flex items-center gap-1.5",
                    isDark ? "text-zinc-400 border-zinc-700" : "text-[#9E8778] border-[#EEEAE0]"
                  )}>
                    <Target className="w-3.5 h-3.5" /> Habit
                  </div>
                  {days.map(day => (
                    <div 
                      key={day.toString()} 
                      className={cn(
                        "flex-1 py-2 text-center border-r last:border-r-0 min-w-[55px]",
                        isDark ? "border-zinc-700" : "border-[#EEEAE0]",
                        isToday(day) && (isDark ? "bg-[#F4F3EE]0/10" : "bg-[#F4F3EE]")
                      )}
                    >
                      <div className={cn(
                        "text-[10px] font-medium uppercase",
                        isToday(day) ? "text-[#5F5F5F]" : (isDark ? "text-zinc-400" : "text-[#9E8778]")
                      )}>
                        {format(day, 'EEE')}
                      </div>
                      <div className={cn(
                        "text-sm font-bold w-6 h-6 mx-auto flex items-center justify-center rounded-full mt-0.5",
                        isToday(day) ? "bg-[#F4F3EE]0 text-white" : (isDark ? "text-zinc-300" : "text-[#5F5F5F]")
                      )}>
                        {format(day, 'd')}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Groups & Goals */}
                {loading ? (
                  <div className="p-8 text-center text-sm text-zinc-400">Loading...</div>
                ) : groups.length === 0 ? (
                  <div className="p-8 text-center">
                    <FolderPlus className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-zinc-300">No Groups Yet</p>
                    <p className="text-xs text-zinc-500 mb-3">Create a group to organize habits</p>
                    <Button size="sm" onClick={() => setIsNewGroupDialogOpen(true)}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Create Group
                    </Button>
                  </div>
                ) : groups.map((group, groupIndex) => {
                  const colors = getGroupColor(groupIndex);
                  return (
                    <div key={group.id} className="border-b border-zinc-700 last:border-b-0">
                      {/* Group Header - Color coded */}
                      <div 
                        className={cn(
                          "flex items-center px-3 py-1.5 cursor-pointer select-none",
                          colors.bg
                        )}
                        onClick={() => toggleGroup(group.id)}
                      >
                        <ChevronDown className={cn(
                          "w-3.5 h-3.5 text-zinc-400 mr-2 transition-transform",
                          !expandedGroups[group.id] && "-rotate-90"
                        )} />
                        <div className={cn("w-1 h-4 rounded-full mr-2", colors.accent)} />
                        <span className={cn("font-medium text-sm", colors.text)}>{group.name}</span>
                        <Badge className={cn("ml-2 text-[10px]", colors.badge)}>
                          {groupedGoals[group.id]?.length || 0}
                        </Badge>
                      </div>

                      {/* Goals Rows */}
                      {expandedGroups[group.id] && (
                        <div className={isDark ? "bg-zinc-900/30" : "bg-white"}>
                          {groupedGoals[group.id]?.length === 0 ? (
                            <div className={cn("py-3 text-center text-xs", isDark ? "text-zinc-500" : "text-[#9E8778]")}>No habits in this group</div>
                          ) : groupedGoals[group.id]?.map((goal, idx) => (
                            <div 
                              key={goal.id} 
                              className={cn(
                                "flex group",
                                isDark ? "hover:bg-zinc-800/50" : "hover:bg-[#F4F3EE]/50",
                                idx > 0 && (isDark ? "border-t border-zinc-700/50" : "border-t border-[#EEEAE0]")
                              )}
                            >
                              <div className={cn(
                                "w-[160px] px-3 py-1.5 text-sm border-r flex items-center justify-between",
                                isDark ? "border-zinc-700/50" : "border-[#EEEAE0]"
                              )}>
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <div className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", colors.accent)} />
                                  <span className={cn("truncate text-xs font-medium", isDark ? "text-zinc-300" : "text-[#1A1A1A]")}>{goal.title}</span>
                                </div>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-5 w-5 opacity-0 group-hover:opacity-100 flex-shrink-0" 
                                  onClick={(e) => { e.stopPropagation(); openEditGoal(goal); }}
                                >
                                  <Edit2 className="w-3 h-3 text-zinc-400" />
                                </Button>
                              </div>
                              
                              {days.map(day => {
                                const dateStr = format(day, 'yyyy-MM-dd');
                                const logEntry = goal.daily_logs?.find(l => l.date === dateStr);
                                const isLogged = logEntry && logEntry.value > 0;
                                const isClickable = !isFuture(day);
                                const isTodayCell = isToday(day);

                                return (
                                  <div 
                                    key={day.toString()} 
                                    className={cn(
                                      "flex-1 border-r border-zinc-700/50 last:border-r-0 min-w-[55px] p-1 flex items-center justify-center",
                                      isClickable && "cursor-pointer hover:bg-zinc-700/30",
                                      isTodayCell && "bg-[#F4F3EE]0/10",
                                      !isClickable && "opacity-40"
                                    )}
                                    onClick={() => isClickable && handleToggleLog(goal, day)}
                                  >
                                    {isLogged ? (
                                      goal.tracking_type === 'count' ? (
                                        <div className="w-7 h-7 rounded-md bg-gradient-to-br from-blue-400 to-blue-600 text-white font-bold flex items-center justify-center text-xs shadow-sm">
                                          {logEntry.value}
                                        </div>
                                      ) : (
                                        <div className="w-7 h-7 rounded-md bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-sm">
                                          <Check className="w-4 h-4 text-white" strokeWidth={3} />
                                        </div>
                                      )
                                    ) : (
                                      <div className={cn(
                                        "w-7 h-7 rounded-md border-2 border-dashed flex items-center justify-center",
                                        isClickable ? "border-zinc-600 hover:border-violet-400 hover:bg-[#F4F3EE]0/10" : "border-zinc-700"
                                      )}>
                                        <Plus className="w-3 h-3 text-zinc-500" />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                          
                          {/* Add Habit Button */}
                          <div className="px-3 py-1.5 border-t border-zinc-700/50">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-xs text-zinc-400 hover:text-violet-400 h-6 w-full justify-start"
                              onClick={() => navigate(`/create-goal?group_id=${group.id}`)}
                            >
                              <Plus className="w-3 h-3 mr-1" /> Add habit
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Shared Goals Section */}
                {sharedGoals.length > 0 && (
                  <div className="border-b border-zinc-700 last:border-b-0">
                    {/* Shared Goals Header */}
                    <div 
                      className={cn(
                        "flex items-center px-3 py-1.5 cursor-pointer select-none",
                        isDark ? "bg-gradient-to-r from-[#F4F3EE] to-[#F4F3EE]" : "bg-gradient-to-r from-[#F4F3EE] to-[#F4F3EE]"
                      )}
                      onClick={() => toggleGroup('shared-goals')}
                    >
                      <ChevronDown className={cn(
                        "w-3.5 h-3.5 mr-2 transition-transform",
                        isDark ? "text-zinc-400" : "text-[#9E8778]",
                        !expandedGroups['shared-goals'] && "-rotate-90"
                      )} />
                      <div className="w-1 h-4 rounded-full mr-2 bg-gradient-to-b bg-[#1A1A1A]" />
                      <Users className="w-3.5 h-3.5 text-violet-400 mr-1.5" />
                      <span className={cn("font-medium text-sm", isDark ? "text-violet-400" : "text-[#1A1A1A]")}>Shared Goals</span>
                      <Badge className={cn("ml-2 text-[10px]", isDark ? "bg-[#F4F3EE]0/10 text-violet-400 border-violet-500/20" : "bg-[#F4F3EE] text-[#1A1A1A] border-[#E5E2DA]")}>
                        {sharedGoals.length}
                      </Badge>
                    </div>

                    {/* Shared Goals Rows */}
                    {expandedGroups['shared-goals'] && (
                      <div className={isDark ? "bg-zinc-900/30" : "bg-white"}>
                        {sharedGoals.map((goal, idx) => {
                          const isCompleted = (goal.user_progress || 0) >= goal.target_value;
                          return (
                            <div 
                              key={goal.id} 
                              className={cn(
                                "flex group",
                                isDark ? "hover:bg-[#F4F3EE]0/5" : "hover:bg-[#F4F3EE]/50",
                                idx > 0 && (isDark ? "border-t border-zinc-700/50" : "border-t border-[#EEEAE0]")
                              )}
                            >
                              <div 
                                className={cn(
                                  "w-[160px] px-3 py-1.5 text-sm border-r flex items-center gap-1.5 cursor-pointer",
                                  isDark ? "border-zinc-700/50 hover:bg-[#F4F3EE]0/10" : "border-[#EEEAE0] hover:bg-[#F4F3EE]"
                                )}
                                onClick={() => navigate(`/shared-goals/${goal.id}`)}
                              >
                                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-[#F4F3EE]0" />
                                <span className={cn("truncate text-xs font-medium", isDark ? "text-zinc-300" : "text-[#1A1A1A]")}>{goal.title}</span>
                              </div>
                              
                              {/* Progress indicator */}
                              <div className="flex-1 flex items-center px-3 py-1.5 gap-2">
                                <div className={cn("flex items-center gap-1.5 text-xs", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>
                                  <Target className="w-3.5 h-3.5 text-violet-400" />
                                  <span className={cn("font-medium", isDark ? "text-zinc-300" : "text-[#1A1A1A]")}>{goal.user_progress || 0}/{goal.target_value}</span>
                                  <span className={isDark ? "text-zinc-500" : "text-[#9E8778]"}>{goal.target_unit}</span>
                                </div>
                                <Progress 
                                  value={((goal.user_progress || 0) / goal.target_value) * 100} 
                                  className="flex-1 h-1.5 max-w-[100px]" 
                                />
                                <div className={cn("flex items-center gap-1 text-xs", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>
                                  <Users className="w-3 h-3" />
                                  <span>{goal.participant_count}</span>
                                </div>
                                
                                {/* Log Button */}
                                {goal.status === 'ACTIVE' && !isCompleted ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-2 text-[10px] text-violet-400 hover:bg-[#F4F3EE]0/20 hover:text-violet-300"
                                    onClick={(e) => handleSharedGoalLog(goal.id, e)}
                                    data-testid={`weekly-shared-log-${goal.id}`}
                                  >
                                    <Plus className="w-3 h-3 mr-0.5" /> Log
                                  </Button>
                                ) : isCompleted ? (
                                  <span className="text-[10px] text-emerald-400 flex items-center gap-0.5">
                                    <Check className="w-3 h-3" /> Done
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* RIGHT: Manage Goals by Group */}
          <div className="space-y-3">
            <Card className={cn("shadow-sm", isDark ? "glass-card" : "bg-white border-[#EEEAE0]")}>
              <div className={cn(
                "p-3 border-b",
                isDark ? "border-zinc-700 bg-zinc-800/50" : "border-[#EEEAE0] bg-[#F4F3EE]/30"
              )}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-violet-400" />
                    <span className={cn("font-medium text-sm", isDark ? "text-white" : "text-[#1A1A1A]")}>Manage Goals by Group</span>
                  </div>
                  <Badge variant="outline" className={cn("text-xs", isDark ? "border-zinc-700 text-zinc-300" : "border-[#E5E2DA] text-[#5F5F5F]")}>{activeGoals.length} active</Badge>
                </div>
                <p className={cn("text-xs mt-1", isDark ? "text-zinc-500" : "text-[#9E8778]")}>Drag goals between groups or use dropdown</p>
              </div>
              
              <ScrollArea className="h-[calc(100vh-320px)] min-h-[300px]">
                <div className="p-3 space-y-3">
                  {groups.map((group, groupIndex) => {
                    const colors = getGroupColor(groupIndex);
                    return (
                      <Card 
                        key={group.id}
                        className={cn(
                          "border-2 transition-all overflow-hidden",
                          draggedGoal && draggedGoal.group_id !== group.id
                            ? "border-dashed border-violet-500/50 bg-[#F4F3EE]0/10"
                            : cn(colors.border, colors.bg)
                        )}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, group.id)}
                      >
                        <div className={cn("px-3 py-2 border-b", colors.border, colors.bg)}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={cn("w-2 h-2 rounded-full", colors.accent)} />
                              <span className={cn("font-medium text-sm", colors.text)}>{group.name}</span>
                            </div>
                            <Badge className={cn("text-[10px]", colors.badge)}>
                              {groupedGoals[group.id]?.length || 0}
                            </Badge>
                          </div>
                        </div>
                        <CardContent className="p-2 min-h-[60px]">
                          {groupedGoals[group.id]?.length === 0 ? (
                            <div className={cn("text-center py-3 text-xs", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
                              Drop goals here
                            </div>
                          ) : (
                            <div className="space-y-1.5">
                              {groupedGoals[group.id]?.map(goal => (
                                <div
                                  key={goal.id}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, goal)}
                                  onDragEnd={handleDragEnd}
                                  className={cn(
                                    "flex items-center gap-2 p-2 rounded-md border cursor-grab active:cursor-grabbing transition-all",
                                    isDark ? "bg-zinc-800/50" : "bg-white",
                                    draggedGoal?.id === goal.id 
                                      ? "opacity-50 border-violet-500 shadow-md" 
                                      : isDark ? "border-zinc-700 hover:border-zinc-600 hover:shadow-sm" : "border-[#E5E2DA] hover:border-violet-300 hover:shadow-sm"
                                  )}
                                >
                                  <GripVertical className={cn("w-3.5 h-3.5 flex-shrink-0", isDark ? "text-zinc-500" : "text-[#9E8778]")} />
                                  <div className="flex-1 min-w-0">
                                    <p className={cn("text-xs font-medium truncate", isDark ? "text-zinc-300" : "text-[#1A1A1A]")}>{goal.title}</p>
                                    <p className={cn("text-[10px]", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
                                      {goal.progress || 0}/{goal.target_count} • ${goal.penalty_amount}
                                    </p>
                                  </div>
                                  <Select
                                    value={goal.group_id}
                                    onValueChange={(newGroupId) => handleChangeGroup(goal.id, newGroupId)}
                                  >
                                    <SelectTrigger className={cn("w-auto h-6 text-[10px] border-0 px-1.5", isDark ? "bg-zinc-700" : "bg-[#F4F3EE]")}>
                                      <ArrowRight className={cn("w-3 h-3", isDark ? "text-zinc-400" : "text-[#5F5F5F]")} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {groups.map(g => (
                                        <SelectItem key={g.id} value={g.id} className="text-xs">
                                          {g.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                  
                  {/* Add Group Button */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setIsNewGroupDialogOpen(true)}
                    className="w-full border-dashed border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-600 text-xs h-9"
                  >
                    <FolderPlus className="w-4 h-4 mr-1" /> Add New Group
                  </Button>
                </div>
              </ScrollArea>
            </Card>
          </div>
        </div>

        {/* Past Challenges */}
        {pastGoals.length > 0 && (
          <PastGoalsSection goals={pastGoals} />
        )}
      </div>

      {/* New Group Dialog */}
      <Dialog open={isNewGroupDialogOpen} onOpenChange={setIsNewGroupDialogOpen}>
        <DialogContent className="sm:max-w-[425px] bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <FolderPlus className="w-5 h-5 text-violet-400" />
              Create New Group
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Groups help you organize related habits together.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-sm font-medium text-zinc-300">Group Name</Label>
            <Input 
              value={newGroupName} 
              onChange={e => setNewGroupName(e.target.value)} 
              placeholder="e.g., Morning Routine, Health, Work..." 
              className="mt-2 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              onKeyDown={e => e.key === 'Enter' && createGroup()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNewGroupDialogOpen(false)} className="border-zinc-700 text-zinc-300 hover:bg-white/10">Cancel</Button>
            <Button 
              onClick={createGroup}
              disabled={!newGroupName.trim()}
              className="btn-glossy"
            >
              Create Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Goal Dialog */}
      <Dialog open={editGoalOpen} onOpenChange={setEditGoalOpen}>
        <DialogContent className="sm:max-w-[425px] bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Edit2 className="w-5 h-5 text-violet-400" />
              Edit Goal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Title</Label>
              <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Target Count</Label>
              <Input type="number" value={editTarget} onChange={e => setEditTarget(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !editEndDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editEndDate ? format(editEndDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={editEndDate}
                    onSelect={(date) => date && setEditEndDate(date)}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditGoalOpen(false)}>Cancel</Button>
            <Button onClick={saveEditGoal} className="btn-glossy">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Collapsible Past Goals Section
function PastGoalsSection({ goals }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="glass-card overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-zinc-800/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ChevronDown className={cn("w-3.5 h-3.5 text-zinc-500 transition-transform", !isExpanded && "-rotate-90")} />
          <Archive className="w-4 h-4 text-zinc-500" />
          <span className="text-xs font-medium text-zinc-300">Past Challenges</span>
          <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-400">{goals.length}</Badge>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-zinc-500">
          <Lock className="w-3 h-3" /> Read-only
        </div>
      </button>
      
      {isExpanded && (
        <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {goals.map(goal => (
            <div 
              key={goal.id}
              className={cn(
                "flex items-center gap-2 p-2 rounded-lg bg-zinc-800/50 border text-xs",
                goal.status === 'COMPLETED' ? "border-emerald-500/30" : "border-zinc-700"
              )}
            >
              <div className={cn(
                "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0",
                goal.status === 'COMPLETED' ? "bg-emerald-500/20" : "bg-zinc-700"
              )}>
                {goal.status === 'COMPLETED' ? (
                  <Trophy className="w-3 h-3 text-emerald-400" />
                ) : (
                  <Ban className="w-3 h-3 text-zinc-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate text-zinc-200">{goal.title}</p>
                <p className="text-[10px] text-zinc-500">Target: {goal.target_count}</p>
              </div>
              <Badge className={cn(
                "text-[10px]",
                goal.status === 'COMPLETED' ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-700 text-zinc-400"
              )}>
                {goal.status === 'COMPLETED' ? 'Won' : 'Stopped'}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
